"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "@/context/auth-context"
import { useTheme } from "next-themes"
import { useNotifications } from "@/hooks/useNotifications"
import { isOwner, isSuperAdmin } from "@/lib/roles"

import {
  LayoutDashboard,
  Kanban,
  Bell,
  LogOut,
  MapPin,
  Moon,
  Sun,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar,
  WhatsappLogo,
  Smartphone,
  FileText
} from "@/lib/icons"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { NotificationPanel } from "@/components/notifications/notification-panel"
import { NotificationSound } from "@/components/notifications/notification-sound"

// Admines supremos
const OWNER_EMAILS = new Set([
  "admin@tepeyac.mx",
  "ramiro@tepeyact.com",
])

// Lista de centros
const CENTERS = [
  "Todos",
  "Guadalajara",
  "Puerto Vallarta",
  "México",
  "Monterrey",
  "Puebla",
  "Cancún",
  "Querétaro",
  "León",
  "Toluca",
  "Aguascalientes",
  "Morelia",
  "Tepic",
]

const NavItem = ({ to, icon: Icon, label, alertCount = 0, onClick, expanded = true, active = false }) => {
  // Si viene un onClick se ejecuta para cierre de mobile menu o notificaciones
  const handleClick = () => {
    onClick?.()
  }

  // Determine content
  const content = (
    <>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all duration-500 ${active
        ? "bg-primary text-white shadow-lg shadow-primary/25 scale-105"
        : "bg-transparent text-slate-500 group-hover:bg-slate-100 group-hover:text-slate-900 group-hover:scale-110 dark:text-slate-400 dark:group-hover:bg-slate-800 dark:group-hover:text-slate-100"
        }`}>
        <Icon className="h-5 w-5 transition-transform duration-500 group-hover:rotate-6" />
        {alertCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3 rounded-full bg-red-500 border-2 border-white dark:border-slate-900 animate-pulse shadow-sm" />
        )}
      </div>

      <AnimatePresence mode="wait">
        {expanded && (
          <motion.div
            key="label"
            initial={{ opacity: 0, x: -8, filter: "blur(4px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, x: -8, filter: "blur(4px)" }}
            transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
            className="flex-1 flex items-center justify-between min-w-0"
          >
            <span className={`font-semibold tracking-tight transition-colors duration-300 truncate ${active ? "text-primary dark:text-primary" : "text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-100"
              }`}>
              {label}
            </span>

            {alertCount > 0 && (
              <motion.span
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white shadow-sm ring-2 ring-white dark:ring-slate-900"
              >
                {alertCount > 9 ? '9+' : alertCount}
              </motion.span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )

  const baseClass = `group relative flex w-full items-center ${!expanded ? 'justify-center' : 'gap-3'} rounded-2xl p-2 transition-all duration-500 ${active ? "bg-primary/5 dark:bg-primary/10" : "hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
    }`

  if (to) {
    return (
      <Link to={to} onClick={handleClick} className={baseClass}>
        {content}
      </Link>
    )
  }

  return (
    <button type="button" onClick={handleClick} className={baseClass}>
      {content}
    </button>
  )
}

export function Sidebar({ children, onLogout }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()

  const [showNotifications, setShowNotifications] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "true" } catch { return false }
  })

  const toggleSidebar = () => {
    setIsCollapsed(prev => {
      const next = !prev
      try { localStorage.setItem("sidebar_collapsed", String(next)) } catch { }
      return next
    })
  }

  const {
    notifications,
    unreadCount,
    loading,
    playSoundType,
    markAsRead,
    markAllAsRead,
    dismissNotification
  } = useNotifications(user)

  // Sincronizar el contador con otros componentes (KanbanPage usa esto para su botón 🔔)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("notificationsCountSync", { detail: { count: unreadCount || 0 } }))
  }, [unreadCount])

  // Permitir abrir el panel de notificaciones desde otros componentes
  useEffect(() => {
    const handler = () => setShowNotifications(true)
    window.addEventListener("openNotificationsPanel", handler)
    return () => window.removeEventListener("openNotificationsPanel", handler)
  }, [])

  // Seleccionar logo según el tema. Forzamos un tamaño idéntico en el CSS.
  const logoSrc = resolvedTheme === "dark"
    ? "/IT_Innovation_School_blanco.png"
    : "/it-logo.webp"

  // Reloj en tiempo real
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000) // actualiza cada 30s
    return () => clearInterval(t)
  }, [])

  const superAdmin = isSuperAdmin(user)
  const isAdmin = Boolean(user?.isAdmin || user?.rol === "admin" || OWNER_EMAILS.has(String(user?.email || "").toLowerCase()) || superAdmin)
  const owner = isOwner(user) || OWNER_EMAILS.has(String(user?.email || "").toLowerCase()) || superAdmin

  // Campus seleccionado
  const [selectedCampus, setSelectedCampus] = useState(() => {
    try { return localStorage.getItem("selected_campus") || "Todos" } catch { return "Todos" }
  })

  const onChangeCampus = (c) => {
    try { localStorage.setItem("selected_campus", c) } catch { }
    setSelectedCampus(c)
    window.dispatchEvent(new CustomEvent("campusChanged", { detail: { campus: c } }))
  }

  const handleLogout = async () => {
    await logout()
    onLogout?.()
    navigate("/", { replace: true })
  }

  const handleNotificationClick = (notification) => {
    if (!notification) return

    if (notification.leadId) {
      try {
        localStorage.setItem('open_conversation_lead', notification.leadId)
        localStorage.setItem('highlight_lead', notification.leadId)
        window.dispatchEvent(new CustomEvent("openLeadChat", { detail: { leadId: notification.leadId } }))
        window.dispatchEvent(new CustomEvent("highlightLead", { detail: { leadId: notification.leadId } }))
      } catch { }
      navigate('/kanban')
    }

    setShowNotifications(false)
    setMobileMenuOpen(false)
  }

  const isActiveRoute = (path) => location.pathname === path



  return (
    <div className="flex h-screen w-full overflow-hidden bg-slate-50 dark:bg-slate-950 selection:bg-primary/20">
      {/* Sound component */}
      {playSoundType && !showNotifications && (
        <NotificationSound play={true} type={playSoundType} />
      )}

      {/* ========================================= */}
      {/* DESKTOP SIDEBAR */}
      {/* ========================================= */}
      <motion.aside
        initial={false}
        animate={{
          width: isCollapsed ? 80 : 260,
          backgroundColor: resolvedTheme === 'dark' ? "rgba(2, 8, 23, 0.8)" : "rgba(255, 255, 255, 0.8)"
        }}
        transition={{ type: "spring", stiffness: 200, damping: 25 }}
        className="hidden md:flex flex-col border-r border-slate-200/50 dark:border-slate-800/50 backdrop-blur-xl shadow-2xl shadow-slate-200/20 dark:shadow-none z-20 relative"
      >

        {/* TOGGLE BUTTON */}
        <button
          onClick={toggleSidebar}
          className="absolute -right-4 top-10 flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-md hover:text-primary hover:scale-110 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 z-50 transition-all hover:ring-2 hover:ring-primary/20"
        >
          {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>

        {/* LOGO SUPERIOR */}
        <div className={`flex shrink-0 items-center justify-center border-b border-transparent transition-all duration-300 ${isCollapsed ? 'h-[80px] p-2' : 'h-[120px] p-4'}`}>
          {isCollapsed ? (
            <img
              src="/logo-tepeyac.png"
              alt="Logo Tepeyac"
              className="w-14 h-14 object-contain drop-shadow-sm transition-opacity duration-300"
            />
          ) : (
            <img
              src={logoSrc}
              alt="IT Innovation School"
              className="w-full max-w-[140px] h-[65px] object-contain transition-opacity duration-300"
            />
          )}
        </div>

        {/* Fecha y hora */}
        <div className={`shrink-0 flex items-center justify-center gap-1.5 py-2 border-b border-slate-100 dark:border-slate-800/50 text-slate-500 dark:text-slate-400 ${isCollapsed ? 'px-1' : 'px-4'}`}>
          {isCollapsed ? (
            <span className="text-[10px] font-medium tabular-nums" title={now.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}>
              {now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
            </span>
          ) : (
            <>
              <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="text-xs font-medium capitalize">
                {now.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" })}
              </span>
              <span className="text-[10px] text-slate-400 dark:text-slate-500 tabular-nums">
                {now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
              </span>
            </>
          )}
        </div>

        {/* CONTENIDO SCROLLABLE (Menú) */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-4 custom-scrollbar">

          {/* Navegación Principal */}
          <div className="space-y-1">
            {!isCollapsed && <p className="px-3 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3">Menú Principal</p>}
            {isAdmin && <NavItem to="/admin" icon={LayoutDashboard} label="Dashboard" expanded={!isCollapsed} active={isActiveRoute("/admin")} />}
            <NavItem to="/kanban" icon={Kanban} label="Kanban" expanded={!isCollapsed} active={isActiveRoute("/kanban")} />
            <NavItem to="/leads" icon={FileText} label="Base de datos" expanded={!isCollapsed} active={isActiveRoute("/leads")} />
            <NavItem
              icon={Bell}
              label="Notificaciones"
              alertCount={unreadCount}
              onClick={() => setShowNotifications(true)}
              expanded={!isCollapsed}
            />
            {superAdmin && <NavItem to="/admin/evolution" icon={WhatsappLogo} label="WhatsApp" expanded={!isCollapsed} active={isActiveRoute("/admin/evolution")} />}
          </div>

          {/* Campus Selector (Owner) */}
          {owner && (
            <div className={`px-1 ${isCollapsed ? 'flex justify-center mt-4' : ''}`}>
              {!isCollapsed && <p className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3 mt-4">Campus Activo</p>}
              {isCollapsed ? (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500" title={`Campus: ${selectedCampus}`}>
                  <MapPin className="h-5 w-5" />
                </div>
              ) : (
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <select
                    value={selectedCampus}
                    onChange={(e) => onChangeCampus(e.target.value)}
                    className="w-full h-10 appearance-none rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm font-medium text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200"
                  >
                    {CENTERS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {!owner && user?.campus && (
            <div className={`px-1 ${isCollapsed ? 'flex justify-center mt-4' : ''}`}>
              {!isCollapsed && <p className="px-2 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3 mt-4">Campus Activo</p>}
              {isCollapsed ? (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-primary" title={user.campus}>
                  <MapPin className="h-5 w-5" />
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-3">
                  <MapPin className="h-4 w-4 text-primary" />
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 truncate">{user.campus}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* BOTTOM SECTION (Perfil, Theme, Logout) */}
        <div className="shrink-0 border-t border-slate-100 dark:border-slate-800 p-3 lg:p-4 space-y-3 bg-slate-50/50 dark:bg-slate-900/20">

          {/* Theme Toggle */}
          <div className={`flex items-center justify-center p-1 rounded-full bg-slate-200/60 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 shadow-inner mx-auto ${isCollapsed ? 'w-10 h-10' : 'max-w-fit'}`}>
            {isCollapsed ? (
              <button
                onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                className={`flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 bg-white dark:bg-slate-900 shadow-sm ${resolvedTheme === 'dark' ? 'text-indigo-400' : 'text-amber-500'
                  }`}
                title={resolvedTheme === 'dark' ? "Cambiar a Modo Claro" : "Cambiar a Modo Oscuro"}
              >
                {resolvedTheme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </button>
            ) : (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setTheme('light')}
                  className={`flex h-8 w-16 items-center justify-center rounded-full transition-all duration-300 ${resolvedTheme !== 'dark'
                    ? 'bg-white shadow text-amber-500 font-bold scale-100'
                    : 'text-slate-400 hover:text-slate-600 scale-95'
                    }`}
                  title="Modo Claro"
                >
                  <Sun className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setTheme('dark')}
                  className={`flex h-8 w-16 items-center justify-center rounded-full transition-all duration-300 ${resolvedTheme === 'dark'
                    ? 'bg-slate-900 shadow-md text-indigo-400 font-bold scale-100 border border-slate-700'
                    : 'text-slate-400 hover:text-slate-600 scale-95'
                    }`}
                  title="Modo Oscuro"
                >
                  <Moon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* User Card */}
          {user && (
            <div className={`flex items-center ${isCollapsed ? 'justify-center border-transparent bg-transparent shadow-none py-1' : 'gap-3 px-3 rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 py-3 shadow-sm'}`}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold text-lg shadow-md" title={user.nombre}>
                {user.nombre?.charAt(0).toUpperCase()}
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-bold text-slate-900 dark:text-slate-100 leading-tight">
                    {user.nombre}
                  </p>
                  <Badge variant={isAdmin ? "default" : "outline"} className={`mt-1 text-[9px] px-1.5 py-0 uppercase tracking-widest ${isAdmin ? 'bg-primary/10 text-primary border-primary/20' : ''}`}>
                    {isAdmin ? "Admin" : "Asesor"}
                  </Badge>
                </div>
              )}
            </div>
          )}

          {/* Logout Button */}
          {isCollapsed ? (
            <button
              className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors mx-auto"
              onClick={handleLogout}
              title="Cerrar Sesión"
            >
              <LogOut className="h-5 w-5" />
            </button>
          ) : (
            <Button
              variant="outline"
              className={`w-full rounded-xl border-red-200 bg-red-50/50 text-red-600 hover:bg-red-100 hover:text-red-700 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-400 shadow-sm transition-all ${isCollapsed ? 'px-0 justify-center h-10 w-10 mx-auto' : 'gap-2'}`}
              onClick={handleLogout}
              title="Cerrar Sesión"
            >
              <LogOut className="h-4 w-4" />
              {!isCollapsed && <span>Cerrar Sesión</span>}
            </Button>
          )}
        </div>
      </motion.aside>

      {/* ========================================= */}
      {/* MAIN CONTENT AREA */}
      {/* ========================================= */}
      <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden relative">

        {/* MOBILE HEADER — compact */}
        <header className="md:hidden flex h-12 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 dark:border-slate-800 dark:bg-slate-950 z-20">
          <img src={logoSrc} alt="Logo" className="h-8 w-auto object-contain" />
          <div className="flex items-center gap-1.5">
            {user && (
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold">
                {user.nombre?.charAt(0).toUpperCase()}
              </div>
            )}
            <button
              onClick={() => { resolvedTheme === 'dark' ? setTheme('light') : setTheme('dark') }}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </header>

        {/* SCROLLABLE PAGE CONTENT — padding bottom for tab bar */}
        <div className="flex-1 overflow-y-auto w-full custom-scrollbar relative z-10 pb-16 md:pb-0">
          {children}
        </div>

        {/* ========================================= */}
        {/* MOBILE BOTTOM TAB BAR */}
        {/* ========================================= */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 safe-area-bottom">
          <div className="flex items-stretch h-14">
            {/* Kanban tab */}
            <Link
              to="/kanban"
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${isActiveRoute("/kanban") ? "text-primary" : "text-slate-400 dark:text-slate-500"}`}
            >
              <Kanban className="h-5 w-5" />
              <span className="text-[10px] font-semibold">Kanban</span>
            </Link>

            {/* Base de datos tab */}
            <Link
              to="/leads"
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${isActiveRoute("/leads") ? "text-primary" : "text-slate-400 dark:text-slate-500"}`}
            >
              <FileText className="h-5 w-5" />
              <span className="text-[10px] font-semibold">Base</span>
            </Link>

            {/* Notifications tab */}
            <button
              onClick={() => setShowNotifications(true)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 relative transition-colors ${showNotifications ? "text-primary" : unreadCount > 0 ? "text-amber-500" : "text-slate-400 dark:text-slate-500"}`}
            >
              <div className="relative">
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white shadow ring-2 ring-white dark:ring-slate-950">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-semibold">Alertas</span>
            </button>

            {/* Dashboard tab (admin only) */}
            {isAdmin && (
              <Link
                to="/admin"
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${isActiveRoute("/admin") ? "text-primary" : "text-slate-400 dark:text-slate-500"}`}
              >
                <LayoutDashboard className="h-5 w-5" />
                <span className="text-[10px] font-semibold">Admin</span>
              </Link>
            )}

            {/* WhatsApp tab (super admin only) */}
            {superAdmin && (
              <Link
                to="/admin/evolution"
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors ${isActiveRoute("/admin/evolution") ? "text-primary" : "text-slate-400 dark:text-slate-500"}`}
              >
                <Smartphone className="h-5 w-5" />
                <span className="text-[10px] font-semibold">WhatsApp</span>
              </Link>
            )}

            {/* Logout tab */}
            <button
              onClick={handleLogout}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 text-slate-400 dark:text-slate-500"
            >
              <LogOut className="h-5 w-5" />
              <span className="text-[10px] font-semibold">Salir</span>
            </button>
          </div>
        </nav>
      </main>

      {/* NOTIFICATION PANEL DRAWER */}
      {showNotifications && (
        <NotificationPanel
          notifications={notifications}
          unreadCount={unreadCount}
          loading={loading}
          playSoundType={playSoundType}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onDismiss={dismissNotification}
          onClose={() => setShowNotifications(false)}
          onNotificationClick={handleNotificationClick}
        />
      )}
    </div>
  )
}
